import { useState } from 'react';
import type { QboSettings, UpdateQboSettingsInput } from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { useUpdateQboSettings } from './api.js';

interface SettingsEditorProps {
  settings: QboSettings;
  onClose: () => void;
}

const SECRET_PLACEHOLDER = '••••••••';

function labelClass(): string {
  return 'mb-1 text-xs font-medium uppercase tracking-wide text-slate-500';
}

function inputClass(): string {
  return 'rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';
}

/**
 * Admin form for setting up the QuickBooks Online connection entirely from the UI.
 * Plain fields are pre-filled from the current effective configuration; the client
 * secret is write-only (we only show whether one is stored). Only changed fields are
 * sent, using the tri-state contract (`undefined` = unchanged, `null` = clear, value
 * = set).
 */
export function SettingsEditor({ settings, onClose }: SettingsEditorProps): JSX.Element {
  const updateMutation = useUpdateQboSettings();

  const [clientId, setClientId] = useState(settings.clientId ?? '');
  const [redirectUri, setRedirectUri] = useState(settings.redirectUri ?? '');
  const [defaultItemId, setDefaultItemId] = useState(settings.defaultItemId ?? '');
  const [defaultItemName, setDefaultItemName] = useState(settings.defaultItemName ?? '');
  const [overageItemSku, setOverageItemSku] = useState(settings.overageItemSku ?? '');
  const [overageItemClass, setOverageItemClass] = useState(settings.overageItemClass ?? '');
  const [minorVersion, setMinorVersion] = useState(settings.minorVersion ?? '');
  const [dryRun, setDryRun] = useState(settings.dryRun);
  const [clientSecret, setClientSecret] = useState('');
  const [clearClientSecret, setClearClientSecret] = useState(false);

  const canStoreSecrets = settings.canStoreSecrets;
  const suggestedRedirect = `${window.location.origin}/integrations/qbo`;

  const resolvePlain = (next: string, original: string | null): string | null | undefined => {
    const trimmed = next.trim();
    const base = original ?? '';
    if (trimmed === base) return undefined;
    return trimmed === '' ? null : trimmed;
  };

  const resolveSecret = (next: string, clear: boolean): string | null | undefined => {
    if (clear) return null;
    const trimmed = next.trim();
    return trimmed === '' ? undefined : trimmed;
  };

  const buildInput = (): UpdateQboSettingsInput => {
    const input: UpdateQboSettingsInput = {};
    const id = resolvePlain(clientId, settings.clientId);
    if (id !== undefined) input.clientId = id;
    const redirect = resolvePlain(redirectUri, settings.redirectUri);
    if (redirect !== undefined) input.redirectUri = redirect;
    const itemId = resolvePlain(defaultItemId, settings.defaultItemId);
    if (itemId !== undefined) input.defaultItemId = itemId;
    const itemName = resolvePlain(defaultItemName, settings.defaultItemName);
    if (itemName !== undefined) input.defaultItemName = itemName;
    const overageSku = resolvePlain(overageItemSku, settings.overageItemSku);
    if (overageSku !== undefined) input.overageItemSku = overageSku;
    const overageClass = resolvePlain(overageItemClass, settings.overageItemClass);
    if (overageClass !== undefined) input.overageItemClass = overageClass;
    const minor = resolvePlain(minorVersion, settings.minorVersion);
    if (minor !== undefined) input.minorVersion = minor;

    if (dryRun !== settings.dryRun) input.dryRun = dryRun;

    if (canStoreSecrets) {
      const secret = resolveSecret(clientSecret, clearClientSecret);
      if (secret !== undefined) input.clientSecret = secret;
    }
    return input;
  };

  const handleSave = (): void => {
    updateMutation.mutate(buildInput(), {
      onSuccess: () => {
        setClientSecret('');
        setClearClientSecret(false);
        onClose();
      },
    });
  };

  return (
    <div className="mt-4 border-t border-slate-200 pt-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col text-sm">
          <span className={labelClass()}>Client ID</span>
          <input
            type="text"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            autoComplete="off"
            className={inputClass()}
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className={labelClass()}>
            Client secret{' '}
            {settings.hasClientSecret ? <Badge tone="green">stored</Badge> : null}
          </span>
          <input
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            disabled={!canStoreSecrets || clearClientSecret}
            placeholder={settings.hasClientSecret ? SECRET_PLACEHOLDER : ''}
            autoComplete="new-password"
            className={inputClass()}
          />
          {settings.hasClientSecret ? (
            <label className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500">
              <input
                type="checkbox"
                checked={clearClientSecret}
                onChange={(e) => setClearClientSecret(e.target.checked)}
              />
              Clear stored client secret
            </label>
          ) : null}
        </label>
        <label className="flex flex-col text-sm sm:col-span-2">
          <span className={labelClass()}>Redirect URI</span>
          <input
            type="text"
            value={redirectUri}
            onChange={(e) => setRedirectUri(e.target.value)}
            placeholder={suggestedRedirect}
            className={inputClass()}
          />
          <span className="mt-1 text-xs text-slate-500">
            Must exactly match a redirect URI registered on your Intuit app.{' '}
            {redirectUri.trim() === '' ? (
              <button
                type="button"
                onClick={() => setRedirectUri(suggestedRedirect)}
                className="font-medium text-brand-600 hover:underline"
              >
                Use {suggestedRedirect}
              </button>
            ) : null}
          </span>
        </label>
        <label className="flex flex-col text-sm">
          <span className={labelClass()}>Default item ID</span>
          <input
            type="text"
            value={defaultItemId}
            onChange={(e) => setDefaultItemId(e.target.value)}
            className={inputClass()}
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className={labelClass()}>Default item name</span>
          <input
            type="text"
            value={defaultItemName}
            onChange={(e) => setDefaultItemName(e.target.value)}
            className={inputClass()}
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className={labelClass()}>Overage item SKU</span>
          <input
            type="text"
            value={overageItemSku}
            onChange={(e) => setOverageItemSku(e.target.value)}
            placeholder="QuickBooks item name for managed-services overage"
            className={inputClass()}
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className={labelClass()}>Overage class</span>
          <input
            type="text"
            value={overageItemClass}
            onChange={(e) => setOverageItemClass(e.target.value)}
            placeholder="QuickBooks class for managed-services overage"
            className={inputClass()}
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className={labelClass()}>Minor version</span>
          <input
            type="text"
            value={minorVersion}
            onChange={(e) => setMinorVersion(e.target.value)}
            placeholder="e.g. 65"
            className={inputClass()}
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className={labelClass()}>Push mode</span>
          <select
            value={dryRun ? 'dry' : 'live'}
            onChange={(e) => setDryRun(e.target.value === 'dry')}
            className={inputClass()}
          >
            <option value="dry">Dry run (simulated)</option>
            <option value="live">Live (posts to QuickBooks)</option>
          </select>
        </label>
      </div>

      {!canStoreSecrets ? (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Credential storage is disabled because the server has no key to derive from.
          Set <code>QBO_TOKEN_ENCRYPTION_KEY</code> (or any of <code>DATABASE_URL</code>
          /<code>JWT_SECRET</code>) on the server to enter the client secret here.
        </p>
      ) : null}

      {updateMutation.isError ? (
        <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {(updateMutation.error as Error)?.message ?? 'Failed to save connection settings.'}
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={handleSave} isLoading={updateMutation.isPending}>
          Save settings
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
